import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// 页面容器样式
const ApplicationContainer = styled('div', {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '2rem 1rem 4rem',
});

// 页面标题样式
const PageTitle = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  textAlign: 'center',
  color: '$primary',
});

// 页面副标题样式
const PageSubtitle = styled('p', {
  fontSize: '1.2rem',
  marginBottom: '3rem',
  textAlign: 'center',
  color: '$textSecondary',
  maxWidth: '800px',
  margin: '0 auto 3rem',
});

// 应用流程容器
const ProcessSteps = styled(motion.div, {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '4rem',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '2rem',
  }
});

// 流程步骤项
const ProcessStep = styled(motion.div, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '22%',
  position: 'relative',
  
  '@media (max-width: 768px)': {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '1.5rem',
  }
});

// 步骤之间的连接线
const StepConnector = styled('div', {
  position: 'absolute',
  top: '40px',
  right: '-40%',
  width: '80%',
  height: '2px',
  background: '$border',
  zIndex: 1,
  
  '@media (max-width: 768px)': {
    display: 'none',
  }
});

// 步骤图标容器
const StepIconContainer = styled('div', {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: '$primaryLight',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  color: '$primary',
  fontSize: '2rem',
  position: 'relative',
  zIndex: 2,
  
  '@media (max-width: 768px)': {
    width: '60px',
    height: '60px',
    fontSize: '1.5rem',
    marginBottom: '0',
    flexShrink: 0,
  }
});

// 步骤文字容器
const StepTextContainer = styled('div', {
  textAlign: 'center',
  
  '@media (max-width: 768px)': {
    textAlign: 'left',
    flex: 1,
  }
});

// 步骤标题
const StepTitle = styled('h3', {
  fontSize: '1.2rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary',
});

// 步骤描述
const StepDescription = styled('p', {
  fontSize: '0.9rem',
  color: '$textSecondary',
  lineHeight: 1.4,
});

// 表单容器
const ApplicationForm = styled(motion.div, {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2.5rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  
  '@media (max-width: 768px)': {
    padding: '1.5rem',
  }
});

// 表单标题
const FormTitle = styled('h2', {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '$textPrimary',
});

// 表单分组
const FormGroup = styled('div', {
  marginBottom: '2.5rem',
});

// 分组标题
const GroupTitle = styled('h3', {
  fontSize: '1.3rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  padding: '0.5rem 0',
  borderBottom: '1px solid $border',
  color: '$textPrimary',
});

// 表单行
const FormRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1.5rem',
  marginBottom: '1.5rem',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '1rem',
  }
});

// 表单项
const FormField = styled('div', {
  marginBottom: '1.5rem',
});

// 表单标签
const Label = styled('label', {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '500',
  color: '$textPrimary',
});

// 必填标记
const Required = styled('span', {
  color: '$error',
  marginLeft: '0.25rem',
});

// 输入框
const Input = styled('input', {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  border: '1px solid $border',
  borderRadius: '8px',
  background: '$background',
  color: '$textPrimary',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$primary',
    boxShadow: '0 0 0 2px $colors$primaryLight',
  },
  
  '&::placeholder': {
    color: '$textTertiary',
  }
});

// 选择框
const Select = styled('select', {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  border: '1px solid $border',
  borderRadius: '8px',
  background: '$background',
  color: '$textPrimary',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23555%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 1rem top 50%',
  backgroundSize: '0.65rem auto',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$primary',
    boxShadow: '0 0 0 2px $colors$primaryLight',
  }
});

// 文本域
const Textarea = styled('textarea', {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  border: '1px solid $border',
  borderRadius: '8px',
  background: '$background',
  color: '$textPrimary',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  minHeight: '100px',
  resize: 'vertical',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$primary',
    boxShadow: '0 0 0 2px $colors$primaryLight',
  },
  
  '&::placeholder': {
    color: '$textTertiary',
  }
});

// 复选框容器
const CheckboxContainer = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '1rem',
});

// 复选框
const Checkbox = styled('input', {
  marginRight: '0.75rem',
  marginTop: '0.25rem',
});

// 复选框标签
const CheckboxLabel = styled('label', {
  fontSize: '0.95rem',
  lineHeight: 1.5,
  color: '$textSecondary',
});

// 文件上传区
const FileUploadArea = styled('div', {
  border: '2px dashed $border',
  borderRadius: '8px',
  padding: '2rem',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.2s, background-color 0.2s',
  
  '&:hover': {
    borderColor: '$primary',
    background: '$backgroundAlt',
  }
});

// 上传图标样式
const UploadIcon = styled('span', {
  fontSize: '2rem',
  color: '$textTertiary',
  marginBottom: '1rem',
  display: 'block',
});

// 上传说明
const UploadText = styled('p', {
  marginBottom: '0.5rem',
  color: '$textSecondary',
});

// 上传说明小字
const UploadHint = styled('p', {
  fontSize: '0.85rem',
  color: '$textTertiary',
});

// 提交按钮
const SubmitButton = styled('button', {
  padding: '1rem 2.5rem',
  fontSize: '1.1rem',
  fontWeight: '600',
  color: 'white',
  background: '$primary',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s, transform 0.1s',
  display: 'block',
  margin: '2rem auto 0',
  
  '&:hover': {
    background: '$primaryDark',
  },
  
  '&:active': {
    transform: 'translateY(2px)',
  }
});

// 提示块
const InfoBox = styled('div', {
  background: '$infoLight',
  border: '1px solid $info',
  borderRadius: '8px',
  padding: '1.25rem',
  marginBottom: '2rem',
  display: 'flex',
  alignItems: 'flex-start',
});

// 提示图标
const InfoIcon = styled('span', {
  color: '$info',
  fontSize: '1.5rem',
  marginRight: '1rem',
  marginTop: '0.2rem',
  flexShrink: 0,
});

// 提示文本
const InfoText = styled('div', {
  fontSize: '0.95rem',
  color: '$textSecondary',
  lineHeight: 1.5,
});

/**
 * 矿机托管申请组件
 * 提供矿机托管服务申请表单和流程说明
 */
const MiningHotelApplication: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    serviceType: 'standard',
    minerCount: '',
    minerType: '',
    powerConsumption: '',
    startDate: '',
    duration: '6',
    additionalServices: [],
    message: '',
    termsAccepted: false
  });
  
  // 处理表单变更
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // 处理表单提交
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 这里可以添加表单验证和提交逻辑
    console.log('表单数据:', formData);
    alert(t('products.miningHotel.application.formSubmissionMessage', '您的托管申请已成功提交！我们的团队将在24小时内与您联系。'));
  };
  
  // 动画变体
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  return (
    <ApplicationContainer as={motion.div} 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <PageTitle>{t('products.miningHotel.application.title', '矿机托管申请')}</PageTitle>
      <PageSubtitle>
        {t('products.miningHotel.application.subtitle', '通过下面的表单申请我们的矿机托管服务，开始您的专业挖矿之旅')}
      </PageSubtitle>
      
      {/* 申请流程步骤 */}
      <ProcessSteps>
        <ProcessStep variants={itemVariants}>
          <StepIconContainer>
            🖥️
          </StepIconContainer>
          <StepConnector />
          <StepTextContainer>
            <StepTitle>{t('products.miningHotel.application.step1.title', '提交申请')}</StepTitle>
            <StepDescription>
              {t('products.miningHotel.application.step1.description', '填写并提交托管申请表单')}
            </StepDescription>
          </StepTextContainer>
        </ProcessStep>
        
        <ProcessStep variants={itemVariants}>
          <StepIconContainer>
            ✓
          </StepIconContainer>
          <StepConnector />
          <StepTextContainer>
            <StepTitle>{t('products.miningHotel.application.step2.title', '申请审核')}</StepTitle>
            <StepDescription>
              {t('products.miningHotel.application.step2.description', '我们的团队将审核您的申请并联系您')}
            </StepDescription>
          </StepTextContainer>
        </ProcessStep>
        
        <ProcessStep variants={itemVariants}>
          <StepIconContainer>
            💽
          </StepIconContainer>
          <StepConnector />
          <StepTextContainer>
            <StepTitle>{t('products.miningHotel.application.step3.title', '设备接收')}</StepTitle>
            <StepDescription>
              {t('products.miningHotel.application.step3.description', '运送矿机设备或由我们提供设备')}
            </StepDescription>
          </StepTextContainer>
        </ProcessStep>
        
        <ProcessStep variants={itemVariants}>
          <StepIconContainer>
            ⚡
          </StepIconContainer>
          <StepTextContainer>
            <StepTitle>{t('products.miningHotel.application.step4.title', '开始挖矿')}</StepTitle>
            <StepDescription>
              {t('products.miningHotel.application.step4.description', '设备投入运行，开始产生挖矿收益')}
            </StepDescription>
          </StepTextContainer>
        </ProcessStep>
      </ProcessSteps>
      
      {/* 申请表单 */}
      <ApplicationForm as={motion.form} variants={itemVariants} onSubmit={handleSubmit}>
        <FormTitle>{t('products.miningHotel.application.formTitle', '托管服务申请表')}</FormTitle>
        
        <InfoBox>
          <InfoIcon>ℹ️</InfoIcon>
          <InfoText>
            {t('products.miningHotel.application.infoMessage', '提交申请后，我们的团队将在24小时内审核您的申请并与您联系，讨论具体细节和下一步。如有紧急查询，请直接联系我们的客户支持团队。')}
          </InfoText>
        </InfoBox>
        
        {/* 个人信息 */}
        <FormGroup>
          <GroupTitle>{t('products.miningHotel.application.personalInfo', '个人信息')}</GroupTitle>
          
          <FormRow>
            <FormField>
              <Label htmlFor="fullName">
                {t('products.miningHotel.application.fullName', '姓名')}
                <Required>*</Required>
              </Label>
              <Input 
                type="text" 
                id="fullName" 
                name="fullName" 
                value={formData.fullName}
                onChange={handleChange}
                placeholder={t('products.miningHotel.application.fullNamePlaceholder', '请输入您的全名')} 
                required 
              />
            </FormField>
            
            <FormField>
              <Label htmlFor="email">
                {t('products.miningHotel.application.email', '电子邮箱')}
                <Required>*</Required>
              </Label>
              <Input 
                type="email" 
                id="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('products.miningHotel.application.emailPlaceholder', '请输入您的电子邮箱')} 
                required 
              />
            </FormField>
          </FormRow>
          
          <FormRow>
            <FormField>
              <Label htmlFor="phone">
                {t('products.miningHotel.application.phone', '电话号码')}
                <Required>*</Required>
              </Label>
              <Input 
                type="tel" 
                id="phone" 
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('products.miningHotel.application.phonePlaceholder', '请输入您的联系电话')} 
                required 
              />
            </FormField>
            
            <FormField>
              <Label htmlFor="company">
                {t('products.miningHotel.application.company', '公司名称')}
              </Label>
              <Input 
                type="text" 
                id="company" 
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={t('products.miningHotel.application.companyPlaceholder', '如适用，请输入公司名称')} 
              />
            </FormField>
          </FormRow>
          
          <FormField>
            <Label htmlFor="country">
              {t('products.miningHotel.application.country', '国家/地区')}
              <Required>*</Required>
            </Label>
            <Select 
              id="country" 
              name="country"
              value={formData.country}
              onChange={handleChange}
              aria-label={t('products.miningHotel.application.country', '国家/地区')}
              required
            >
              <option value="">{t('products.miningHotel.application.selectCountry', '请选择您的国家/地区')}</option>
              <option value="CN">中国</option>
              <option value="US">美国</option>
              <option value="JP">日本</option>
              <option value="KR">韩国</option>
              <option value="SG">新加坡</option>
              <option value="HK">香港</option>
              <option value="TW">台湾</option>
              <option value="other">其他国家/地区</option>
            </Select>
          </FormField>
        </FormGroup>
        
        {/* 托管需求 */}
        <FormGroup>
          <GroupTitle>{t('products.miningHotel.application.hostingRequirements', '托管需求')}</GroupTitle>
          
          <FormField>
            <Label htmlFor="serviceType">
              {t('products.miningHotel.application.serviceType', '服务类型')}
              <Required>*</Required>
            </Label>
            <Select 
              id="serviceType" 
              name="serviceType"
              value={formData.serviceType}
              onChange={handleChange}
              aria-label={t('products.miningHotel.application.serviceType', '服务类型')}
              required
            >
              <option value="basic">{t('products.miningHotel.application.basicPlan', '基础计划')}</option>
              <option value="standard">{t('products.miningHotel.application.standardPlan', '标准计划')}</option>
              <option value="advanced">{t('products.miningHotel.application.advancedPlan', '高级计划')}</option>
              <option value="custom">{t('products.miningHotel.application.customPlan', '定制计划')}</option>
            </Select>
          </FormField>
          
          <FormRow>
            <FormField>
              <Label htmlFor="minerCount">
                {t('products.miningHotel.application.minerCount', '矿机数量')}
                <Required>*</Required>
              </Label>
              <Input 
                type="number" 
                id="minerCount" 
                name="minerCount"
                value={formData.minerCount}
                onChange={handleChange}
                min="1"
                placeholder={t('products.miningHotel.application.enterMinerCount', '请输入矿机数量')} 
                required 
              />
            </FormField>
            
            <FormField>
              <Label htmlFor="minerType">
                {t('products.miningHotel.application.minerType', '矿机型号')}
                <Required>*</Required>
              </Label>
              <Input 
                type="text" 
                id="minerType" 
                name="minerType"
                value={formData.minerType}
                onChange={handleChange}
                placeholder={t('products.miningHotel.application.minerTypePlaceholder', '例如：Antminer S19 Pro')} 
                required 
              />
            </FormField>
          </FormRow>
          
          <FormField>
            <Label htmlFor="powerConsumption">
              {t('products.miningHotel.application.powerConsumption', '每台功耗 (W)')}
              <Required>*</Required>
            </Label>
            <Input 
              type="number" 
              id="powerConsumption" 
              name="powerConsumption"
              value={formData.powerConsumption}
              onChange={handleChange}
              placeholder={t('products.miningHotel.application.powerConsumptionPlaceholder', '请输入每台矿机的功率消耗')} 
              required 
            />
          </FormField>
          
          <FormRow>
            <FormField>
              <Label htmlFor="startDate">
                {t('products.miningHotel.application.startDate', '预期开始日期')}
                <Required>*</Required>
              </Label>
              <Input 
                type="date" 
                id="startDate" 
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required 
              />
            </FormField>
            
            <FormField>
              <Label htmlFor="duration">
                {t('products.miningHotel.application.duration', '合约期限')}
                <Required>*</Required>
              </Label>
              <Select 
                id="duration" 
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                aria-label={t('products.miningHotel.application.duration', '合约期限')}
                required
              >
                <option value="3">{t('products.miningHotel.application.months', '{{count}} 个月', { count: 3 })}</option>
                <option value="6">{t('products.miningHotel.application.months', '{{count}} 个月', { count: 6 })}</option>
                <option value="12">{t('products.miningHotel.application.months', '{{count}} 个月', { count: 12 })}</option>
                <option value="24">{t('products.miningHotel.application.months', '{{count}} 个月', { count: 24 })}</option>
              </Select>
            </FormField>
          </FormRow>
          
          <FormField>
            <Label htmlFor="message">
              {t('products.miningHotel.application.additionalInfo', '附加信息或需求')}
            </Label>
            <Textarea 
              id="message" 
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('products.miningHotel.application.additionalInfoPlaceholder', '请提供有关您托管需求的任何其他详细信息')} 
            />
          </FormField>
        </FormGroup>
        
        {/* 文件上传 */}
        <FormGroup>
          <GroupTitle>{t('products.miningHotel.application.documentation', '附加文件')}</GroupTitle>
          
          <FileUploadArea>
            <UploadIcon>📤</UploadIcon>
            <UploadText>{t('products.miningHotel.application.dragDrop', '拖放文件或点击上传')}</UploadText>
            <UploadHint>{t('products.miningHotel.application.fileTypes', '支持 PDF, JPEG, PNG 格式，最大文件大小 10MB')}</UploadHint>
            <Input type="file" style={{ display: 'none' }} />
          </FileUploadArea>
        </FormGroup>
        
        {/* 条款同意 */}
        <CheckboxContainer>
          <Checkbox 
            type="checkbox" 
            id="termsAccepted" 
            name="termsAccepted"
            checked={formData.termsAccepted}
            onChange={handleChange}
            required 
          />
          <CheckboxLabel htmlFor="termsAccepted">
            {t('products.miningHotel.application.termsAgreement', '我已阅读并同意BestMine的服务条款和隐私政策，并确认提供的所有信息真实准确。')}
          </CheckboxLabel>
        </CheckboxContainer>
        
        <SubmitButton type="submit">
          {t('products.miningHotel.application.submitApplication', '提交申请')}
        </SubmitButton>
      </ApplicationForm>
    </ApplicationContainer>
  );
};

export default MiningHotelApplication; 